import { Controller } from "@hotwired/stimulus"
import { loadMapboxGL, loadMapboxDraw } from "../lib/mapbox_loader"

// Mapbox GL JS implementation - cleaner, faster, better mobile support
// Mapbox is lazy-loaded from CDN to reduce bundle size
export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    calculatePoolAreaUrl: String,
    detectedPolygon: String,
    detectedArea: Number,
    detectedDimensions: String,
    mapboxToken: String
  }

  static targets = ["container", "info", "area", "gallons", "length", "width", "continueBtn", "polygonInput", "poolShape", "poolSurface"]

  async connect() {
    this.map = null
    this.draw = null
    this.currentPolygon = null
    this.defaultPolygon = null
    this.mapboxgl = null
    this.MapboxDraw = null

    await this.loadDependencies()
    this.initializeMap()
  }

  async loadDependencies() {
    this.mapboxgl = await loadMapboxGL()
    this.MapboxDraw = await loadMapboxDraw()
  }

  get brandColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-500').trim() || '#3b82f6'
  }

  get isMobile() {
    return window.matchMedia('(max-width: 768px)').matches
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    this.mapboxgl.accessToken = this.mapboxTokenValue

    // Create map with maximum quality settings
    // Same zoom for mobile and desktop - 19 is optimal for pool tracing
    const defaultZoom = 19

    this.map = new this.mapboxgl.Map({
      container: this.containerTarget,
      style: 'mapbox://styles/mapbox/satellite-streets-v12', // Satellite with street labels
      center: [this.lngValue, this.latValue], // Note: Mapbox uses [lng, lat] order
      zoom: defaultZoom,
      pitch: 0, // 0 = top-down view
      bearing: 0,
      maxZoom: 21, // Allow zooming in closer
      minZoom: 17, // Allow zooming out for context
      // High-quality rendering options
      antialias: true, // Smooth edges
      fadeDuration: 0, // Instant tile loading (no fade animation)
      preserveDrawingBuffer: true // Better quality for screenshots
    })

    // Navigation controls removed - zoom is locked for consistent pool sizing

    // Initialize drawing
    this.map.on('load', () => {
      this.initializeDrawing()
    })
  }

  initializeDrawing() {
    // Get polygon data or create default
    let polygon = this.getPolygonData()

    // Store default for reset
    this.defaultPolygon = JSON.parse(JSON.stringify(polygon))

    // Convert to GeoJSON format for Mapbox
    // GeoJSON polygons must be "closed" - first and last coordinate must be the same
    const coords = polygon.map(p => [p.lng, p.lat])
    coords.push(coords[0]) // Close the polygon

    const geojson = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords] // Note: [lng, lat] order
      },
      properties: {}
    }

    // Mobile-optimized sizes for touch targets
    const vertexRadius = this.isMobile ? 10 : 6
    const vertexStroke = 2
    const lineWidth = this.isMobile ? 3 : 2

    // Initialize Mapbox GL Draw with custom styles
    this.draw = new this.MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      },
      // Increase touch tolerance on mobile for easier vertex selection
      touchEnabled: true,
      clickBuffer: this.isMobile ? 10 : 2,
      touchBuffer: this.isMobile ? 30 : 25,
      styles: [
        // Polygon fill
        {
          'id': 'gl-draw-polygon-fill',
          'type': 'fill',
          'filter': ['all', ['==', '$type', 'Polygon']],
          'paint': {
            'fill-color': this.brandColor,
            'fill-opacity': 0.2
          }
        },
        // Polygon outline
        {
          'id': 'gl-draw-polygon-stroke',
          'type': 'line',
          'filter': ['all', ['==', '$type', 'Polygon']],
          'layout': {
            'line-cap': 'round',
            'line-join': 'round'
          },
          'paint': {
            'line-color': this.brandColor,
            'line-width': lineWidth
          }
        },
        // Vertex points (corners) - larger on mobile for touch
        {
          'id': 'gl-draw-polygon-vertex',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
          'paint': {
            'circle-radius': vertexRadius,
            'circle-color': this.brandColor,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': vertexStroke
          }
        },
        // Midpoints (clickable points on edges to add new vertices)
        // Completely hidden on mobile - filter set to never match anything
        // On desktop, shows small clickable points to add new vertices
        ...(this.isMobile ? [] : [{
          'id': 'gl-draw-polygon-midpoint',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
          'paint': {
            'circle-radius': 4,
            'circle-color': this.brandColor,
            'circle-opacity': 0.6,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1
          }
        }])
      ]
    })

    this.map.addControl(this.draw, 'top-left')

    // Add the default polygon
    const featureIds = this.draw.add(geojson)
    this.currentFeatureId = featureIds[0]

    // Store initial polygon for mobile 4-vertex enforcement
    this.lastValidPolygon = [...coords]

    // Set to direct_select mode (allows dragging vertices immediately)
    this.draw.changeMode('direct_select', { featureId: this.currentFeatureId })

    // Listen for changes - draw.update fires when polygon is modified
    this.map.on('draw.update', (e) => this.handleDrawUpdate(e))
    this.map.on('draw.create', () => this.updatePolygonData())
    this.map.on('draw.delete', () => this.clearPolygonData())

    // Also listen for mode changes and selection changes
    this.map.on('draw.selectionchange', () => this.updatePolygonData())
    this.map.on('draw.modechange', () => this.updatePolygonData())

    // Listen for render events during vertex dragging for real-time updates
    this.map.on('draw.render', () => {
      // Throttle updates to avoid excessive API calls
      if (this.renderTimeout) clearTimeout(this.renderTimeout)
      this.renderTimeout = setTimeout(() => this.updatePolygonData(), 100)
    })

    // Initial calculation
    this.updatePolygonData()
  }

  getPolygonData() {
    // Check for detected polygon
    if (this.hasDetectedPolygonValue && this.detectedPolygonValue) {
      try {
        const polygon = JSON.parse(this.detectedPolygonValue)
        if (polygon && polygon.length > 0) {
          return polygon
        }
      } catch (error) {
        console.error('Error parsing detected polygon:', error)
      }
    }

    // Return default rectangle
    return this.generateDefaultRectangle()
  }

  generateDefaultRectangle() {
    const lat = this.latValue
    const lng = this.lngValue
    // At mid-latitudes (~33°):
    // 1 degree lat ≈ 364,000 feet
    // 1 degree lng ≈ 303,500 feet
    // For a 16ft x 32ft rectangle (~512 sqft medium pool):
    const lat_offset = 0.0000220  // 8 feet north + 8 feet south = 16 feet total
    const lng_offset = 0.0000527  // 16 feet east + 16 feet west = 32 feet total

    return [
      { lat: lat + lat_offset, lng: lng - lng_offset },
      { lat: lat + lat_offset, lng: lng + lng_offset },
      { lat: lat - lat_offset, lng: lng + lng_offset },
      { lat: lat - lat_offset, lng: lng - lng_offset }
    ]
  }

  handleDrawUpdate(e) {
    // On mobile, enforce 4-vertex limit (simple rectangle)
    if (this.isMobile && e.features && e.features.length > 0) {
      const feature = e.features[0]
      const coords = feature.geometry.coordinates[0]
      // coords includes the closing point, so 4 vertices = 5 coordinates
      const vertexCount = coords.length - 1

      if (vertexCount > 4 && this.lastValidPolygon) {
        // Revert to the last valid 4-vertex polygon
        this.draw.delete(feature.id)
        const featureIds = this.draw.add({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [this.lastValidPolygon]
          },
          properties: {}
        })
        this.currentFeatureId = featureIds[0]
        this.draw.changeMode('direct_select', { featureId: this.currentFeatureId })
        return
      }

      // Store this as the last valid polygon if it has 4 or fewer vertices
      if (vertexCount <= 4) {
        this.lastValidPolygon = [...coords]
      }
    }

    this.updatePolygonData()
  }

  updatePolygonData() {
    // Get current polygon from draw control
    const data = this.draw.getAll()

    if (data.features.length === 0) {
      return
    }

    const feature = data.features[0]
    const coordinates = feature.geometry.coordinates[0]

    // Convert back to {lat, lng} format
    const polygon = coordinates.slice(0, -1).map(coord => ({
      lng: coord[0],
      lat: coord[1]
    }))

    // Store in hidden field
    this.polygonInputTarget.value = JSON.stringify(polygon)
    this.currentPolygon = polygon

    // Calculate dimensions
    this.calculatePoolDimensions(polygon)

    // Check if pool type is selected before enabling continue
    this.updateContinueButtonState()
  }

  updateContinueButtonState() {
    // Pool type (surface) is required - check if a valid option is selected
    const poolSurfaceValid = this.hasPoolSurfaceTarget && this.poolSurfaceTarget.value !== ''
    const hasPolygon = this.currentPolygon && this.currentPolygon.length > 0

    const canContinue = poolSurfaceValid && hasPolygon

    // Enable/disable continue button (both hidden target and visible button in bottom bar)
    this.continueBtnTarget.disabled = !canContinue
    const visibleBtn = document.getElementById('visible-continue-btn')
    if (visibleBtn) visibleBtn.disabled = !canContinue
  }

  clearPolygonData() {
    this.polygonInputTarget.value = ''
    this.continueBtnTarget.disabled = true

    // Also disable visible button in bottom bar
    const visibleBtn = document.getElementById('visible-continue-btn')
    if (visibleBtn) visibleBtn.disabled = true

    // Reset dimension values
    this.areaTarget.textContent = '--'
    this.lengthTarget.textContent = '--'
    this.widthTarget.textContent = '--'
    if (this.hasGallonsTarget) {
      this.gallonsTarget.textContent = '--'
    }
  }

  calculatePoolDimensions(polygon) {
    fetch(this.calculatePoolAreaUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ polygon: polygon })
    })
    .then(response => response.json())
    .then(data => {
      const sqft = Math.round(data.area_sqft)
      this.areaTarget.textContent = sqft
      this.lengthTarget.textContent = data.dimensions.length
      this.widthTarget.textContent = data.dimensions.width

      // Calculate gallons: sqft × 5ft avg depth × 7.48 = sqft × 37.4
      if (this.hasGallonsTarget) {
        const gallons = Math.round(sqft * 37.4)
        this.gallonsTarget.textContent = gallons.toLocaleString()
      }
    })
    .catch(error => {
      console.error('Error calculating dimensions:', error)
    })
  }

  zoomIn(event) {
    event.preventDefault()
    if (this.map) {
      this.map.zoomIn()
    }
  }

  zoomOut(event) {
    event.preventDefault()
    if (this.map) {
      this.map.zoomOut()
    }
  }

  resetRectangle(event) {
    event.preventDefault()

    if (!this.defaultPolygon) return

    // Delete current polygon
    this.draw.deleteAll()

    // Add default polygon back (must be closed)
    const coords = this.defaultPolygon.map(p => [p.lng, p.lat])
    coords.push(coords[0]) // Close the polygon

    const geojson = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords]
      },
      properties: {}
    }

    const featureIds = this.draw.add(geojson)
    this.currentFeatureId = featureIds[0]

    // Enable editing
    this.draw.changeMode('direct_select', { featureId: this.currentFeatureId })

    // Update data
    this.updatePolygonData()
  }

  skip(event) {
    event.preventDefault()
    this.polygonInputTarget.value = JSON.stringify([])
    event.target.closest('form').submit()
  }
}
